import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import JSONEditorLib from 'jsoneditor';
import 'jsoneditor/dist/jsoneditor.css';

type JsonEditorInstance = InstanceType<typeof JSONEditorLib> | null;

type UseJsonEditorParams = {
  onChange: (value: Record<string, unknown>) => void;
};

type UseJsonEditorReturn = {
  containerRef: MutableRefObject<HTMLDivElement | null>;
  setExternalJson: (value: Record<string, unknown>) => void;
};

export const useJsonEditor = ({
  onChange,
}: UseJsonEditorParams): UseJsonEditorReturn => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<JsonEditorInstance>(null);
  const isUpdatingRef = useRef(false);
  const [json, setJson] = useState<Record<string, unknown>>({});

  const handleChange = useCallback(
    (updated: Record<string, unknown>) => {
      if (isUpdatingRef.current) {
        return;
      }

      setJson(updated);
      onChange(updated);
    },
    [onChange]
  );

  useEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    const editorInstance = new JSONEditorLib(containerRef.current, {
      mode: 'tree',
      onChangeJSON: handleChange,
    });

    editorRef.current = editorInstance;

    return () => {
      editorInstance.destroy();
      editorRef.current = null;
    };
  }, [handleChange]);

  useEffect(() => {
    if (!editorRef.current) {
      return;
    }

    isUpdatingRef.current = true;
    editorRef.current.update(json);
    isUpdatingRef.current = false;
  }, [json]);

  const setExternalJson = useCallback((value: Record<string, unknown>) => {
    setJson(value);
  }, []);

  return { containerRef, setExternalJson };
};
